/**
 * TREATX-NOTIFICATION-ADMIN-OPS-11B
 * ----------------------------------
 * AdminConsentDashboard
 *
 * READ-ONLY admin overview powered by the canonical ConsentService
 * via the new `GET /api/admin/consent/overview` aggregator. No new
 * consent store, no new audit log, no new workflow.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Shield, ShieldCheck, ShieldOff, Loader2, RefreshCw,
  Users, Building2, AlertTriangle, Clock,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import api from '../../utils/api';

const StatCard = ({ icon: Icon, label, value, testid }) => (
  <div className="bg-white border rounded-2xl p-4" data-testid={testid}>
    <div className="flex items-center gap-2 text-slate-500 text-xs uppercase tracking-wide">
      <Icon className="h-3.5 w-3.5" />
      <span>{label}</span>
    </div>
    <div className="text-2xl font-semibold text-slate-900 mt-1">{value}</div>
  </div>
);

const AdminConsentDashboard = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get('/admin/consent/overview', { params: { window_hours: 168 } });
      setData(r.data || null);
    } catch (e) {
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const stats = data?.stats || {};
  const versions = data?.consent_text_versions || [];
  const recent = data?.recent_events || [];

  return (
    <div className="min-h-screen bg-slate-50" data-testid="consent-dashboard">
      <div className="bg-white border-b">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/treatx-admin/notification-ops')}
              className="p-2 rounded-full hover:bg-slate-100 text-slate-500"
              data-testid="consent-back-btn"
              aria-label="Back"
            >
              <ArrowLeft className="h-4 w-4" />
            </button>
            <div>
              <h1 className="text-xl font-semibold text-slate-900 flex items-center gap-2">
                <Shield className="h-5 w-5 text-blue-600" />
                Consent dashboard
              </h1>
              <p className="text-xs text-slate-500">Read-only view powered by the canonical <code className="font-mono">ConsentService</code>. No new consent store.</p>
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="consent-refresh-btn">
            {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <RefreshCw className="h-4 w-4 mr-2" />}
            Refresh
          </Button>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-6 space-y-6">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <StatCard icon={Users} label="Patient preferences" value={stats.notification_preferences_patient ?? 0} testid="consent-stat-patient" />
          <StatCard icon={Building2} label="Clinic preferences" value={stats.notification_preferences_clinic ?? 0} testid="consent-stat-clinic" />
          <StatCard icon={ShieldOff} label="WhatsApp opt-outs" value={stats.whatsapp_optouts_active ?? 0} testid="consent-stat-wa-optouts" />
          <StatCard icon={ShieldCheck} label="Messaging consents" value={stats.messaging_consents_recorded ?? 0} testid="consent-stat-messaging" />
          <StatCard icon={Shield} label="Total prefs" value={stats.notification_preferences_total ?? 0} testid="consent-stat-total" />
        </div>

        {/* Versions */}
        <div className="bg-white border rounded-2xl p-5" data-testid="consent-versions">
          <h2 className="text-sm font-semibold text-slate-900 mb-3">Active consent text versions</h2>
          {versions.length === 0 ? (
            <p className="text-xs text-slate-500">No versions yet (no shares recorded).</p>
          ) : (
            <ul className="space-y-1.5">
              {versions.map(v => (
                <li key={v} className="text-xs font-mono text-slate-700 flex items-center gap-2">
                  <span className="px-2 py-0.5 rounded bg-blue-50 text-blue-700">{v}</span>
                </li>
              ))}
            </ul>
          )}
          <p className="text-[11px] text-slate-500 mt-3 flex items-center gap-1">
            <AlertTriangle className="h-3 w-3" />
            Versions are extracted from canonical secure-share grants. New versions appear automatically — no manual editor here (immutable text per version).
          </p>
        </div>

        {/* Recent events */}
        <div className="bg-white border rounded-2xl overflow-hidden" data-testid="consent-recent-events">
          <div className="px-5 py-3 border-b">
            <h2 className="text-sm font-semibold text-slate-900 flex items-center gap-2">
              <Clock className="h-4 w-4 text-slate-500" />
              Recent consent events (last 7 days)
            </h2>
          </div>
          <div className="px-5 py-4">
            {loading && <div className="text-xs text-slate-400" data-testid="consent-recent-loading"><Loader2 className="h-3 w-3 animate-spin inline mr-2" /> Loading…</div>}
            {!loading && recent.length === 0 && (
              <p className="text-xs text-slate-500" data-testid="consent-recent-empty">No consent_audit events in the selected window. The canonical store is empty; no parallel audit log exists.</p>
            )}
            {!loading && recent.length > 0 && (
              <ul className="space-y-2">
                {recent.map((e, i) => (
                  <li key={i} className="text-xs flex items-start gap-2 border-b last:border-0 pb-2" data-testid={`consent-recent-${i}`}>
                    <span className="font-mono text-slate-500 shrink-0">{e.timestamp || ''}</span>
                    <span className="text-slate-700">{e.action || e.event || 'event'}</span>
                    {e.actor_id && <span className="text-slate-400 font-mono text-[10px]">actor:{e.actor_id.slice(0, 8)}…</span>}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        <div className="text-xs text-slate-500 bg-slate-100 rounded-xl p-4" data-testid="consent-canonicality-note">
          <p className="font-medium text-slate-700 mb-1">Canonicality note</p>
          <p>This dashboard is a thin read-only surface over the existing canonical stores:</p>
          <ul className="list-disc list-inside mt-1 space-y-0.5">
            <li><code className="font-mono">notification_preferences</code> — per-event preferences (single writer).</li>
            <li><code className="font-mono">whatsapp_optouts</code> — WA opt-out registry.</li>
            <li><code className="font-mono">consent_messaging</code> — per-thread commercial unlock.</li>
            <li><code className="font-mono">consent_audit</code> — append-only audit (write-only here).</li>
          </ul>
          <p className="mt-1">No new collection was introduced by 11B.</p>
        </div>
      </div>
    </div>
  );
};

export default AdminConsentDashboard;
